import type { OnboardingCockpitStatus } from 'src/features/advisor-book-onboarding/constants/cockpit-url-state';
import { formatDistanceToNow } from 'date-fns';

// Shared placeholder for missing/not-yet-tracked cockpit values.
export const EM_DASH = '\u2014';

type CockpitBadgeVariant = 'warning-muted' | 'info-muted' | 'primary-muted' | 'destructive-muted';

type CockpitStatusCopy = {
  readonly label: string;
  readonly variant: CockpitBadgeVariant;
};

// Maps the backend StagedHousehold status onto the cockpit pill. Variants are
// constrained to the Badge variants that exist in Savvy Kit; the prototype's
// "success" reads map onto primary-muted (Savvy green).
export const COCKPIT_STATUS_COPY: Readonly<Record<OnboardingCockpitStatus, CockpitStatusCopy>> = {
  confirm_account: { label: 'Confirm accounts', variant: 'warning-muted' },
  ready_to_send: { label: 'Ready to send', variant: 'warning-muted' },
  awaiting_client: { label: 'Awaiting client', variant: 'info-muted' },
  in_progress: { label: 'In progress', variant: 'info-muted' },
  complete: { label: 'Complete', variant: 'primary-muted' },
  blocked: { label: 'Blocked', variant: 'destructive-muted' },
};

// duplicate_household intentionally omitted — StagingPromoter no longer writes it
// (HAWK-613). Existing DB rows fall through to the raw-string fallback in
// blockedReasonLabelFor.
export const ONBOARDING_BLOCKED_REASONS = [
  'missing_client_info',
  'missing_account_info',
  'awaiting_client_action',
  'signed_loop_primary',
  'signed_loop_bulk',
  'coclient_not_generated',
  'two_fa_blocked',
] as const;

export type OnboardingBlockedReason = (typeof ONBOARDING_BLOCKED_REASONS)[number];

export const BLOCKED_REASON_COPY = {
  missing_client_info: 'Missing Client Info',
  missing_account_info: 'Missing Account Info',
  awaiting_client_action: 'Awaiting Client Action',
  signed_loop_primary: 'Signed Loop (Primary)',
  signed_loop_bulk: 'Signed Loop (Bulk)',
  coclient_not_generated: 'Co-client Not Generated',
  two_fa_blocked: '2FA Blocked',
} as const satisfies Readonly<Record<OnboardingBlockedReason, string>>;

export function isOnboardingBlockedReason(value: string): value is OnboardingBlockedReason {
  for (const reason of ONBOARDING_BLOCKED_REASONS) {
    if (reason === value) {
      return true;
    }
  }
  return false;
}

export function blockedReasonLabelFor(reason: string) {
  if (isOnboardingBlockedReason(reason)) {
    return BLOCKED_REASON_COPY[reason];
  }
  return reason;
}

export type StatusHoverContent = {
  readonly now: string;
  readonly waitingOn: string;
  readonly suggestedNext: string;
};

export type StatusHoverContentParams = {
  readonly accountsTransferred: number;
  readonly accountsTotal: number;
  readonly primaryClientName: string | null;
  readonly lastContactedAt: string | null | undefined;
  readonly blockedReason?: string | null;
};

function clientFirstName(primaryClientName: string | null) {
  if (primaryClientName === null || primaryClientName === '') {
    return 'the client';
  }
  const firstToken = primaryClientName.trim().split(/\s+/)[0];
  if (firstToken === undefined || firstToken === '') {
    return 'the client';
  }
  return firstToken;
}

function relativeLastContact(lastContactedAt: string | null | undefined) {
  if (lastContactedAt === null || lastContactedAt === undefined || lastContactedAt === '') {
    return null;
  }
  const parsed = new Date(lastContactedAt);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return formatDistanceToNow(parsed, { addSuffix: true });
}

function blockedHoverContent(blockedReason: string | null | undefined): StatusHoverContent {
  if (blockedReason === 'missing_client_info') {
    return {
      now: "Accounts are confirmed, but onboarding can't send until contact info is fixed.",
      waitingOn: 'Missing or invalid email / phone on one or more clients.',
      suggestedNext: 'Click Fix contact info to update the bad fields, then send onboarding.',
    };
  }

  // Advisors can only self-serve missing contact info today. For every other
  // blocker, point them at their onboarding manager until we surface richer detail.
  return {
    now: "There's an issue with this household's onboarding.",
    waitingOn: 'Your onboarding manager to clear the blocker.',
    suggestedNext:
      "Reach out to your onboarding manager. They'll dig into what's stuck and get the transition moving again.",
  };
}

function inProgressNow({
  accountsTransferred,
  accountsTotal,
}: {
  readonly accountsTransferred: number;
  readonly accountsTotal: number;
}) {
  if (accountsTransferred > 0 && accountsTotal > 0) {
    return `${accountsTransferred} of ${accountsTotal} accounts have transferred.`;
  }
  if (accountsTotal > 0) {
    const noun = accountsTotal === 1 ? 'account' : 'accounts';
    const verb = accountsTotal === 1 ? 'is' : 'are';
    return `${accountsTotal} ${noun} ${verb} transferring.`;
  }
  return 'Accounts are transferring.';
}

export function statusHoverContent(
  status: OnboardingCockpitStatus,
  params: StatusHoverContentParams,
): StatusHoverContent | null {
  switch (status) {
    case 'confirm_account':
      return {
        now: 'Accounts pulled from the spreadsheet need to be verified against the custodian.',
        waitingOn: 'You confirming the accounts on this household.',
        suggestedNext: 'Open the row and click Confirm accounts to step through them.',
      };
    case 'ready_to_send':
      return {
        now: 'Accounts confirmed. The onboarding flow is staged and ready to send.',
        waitingOn: 'You clicking Send onboarding.',
        suggestedNext: 'Send onboarding so the client can fill in their info and sign.',
      };
    case 'in_progress':
      return {
        now: inProgressNow(params),
        waitingOn: 'The custodians to complete the remaining asset transfers.',
        suggestedNext: 'Open the transfer dashboard, or contact ops if a transfer is stuck.',
      };
    case 'blocked':
      return blockedHoverContent(params.blockedReason);
    case 'awaiting_client': {
      const firstName = clientFirstName(params.primaryClientName);
      const lastContact = relativeLastContact(params.lastContactedAt);
      const now =
        lastContact === null
          ? `Onboarding delivered to ${firstName}'s inbox.`
          : `Onboarding delivered to ${firstName}'s inbox. Last contacted ${lastContact}.`;
      return {
        now,
        waitingOn: `${firstName === 'the client' ? 'The client' : firstName} to sign. No signatures back yet.`,
        suggestedNext: "Send a reminder if it's been more than a few days.",
      };
    }
    case 'complete':
      return {
        now: 'Onboarding is complete. Accounts are open, signed, and live.',
        waitingOn: 'Nothing. Paperwork is done.',
        suggestedNext: 'Open the household profile to see the full record.',
      };
    default: {
      const _exhaustive: never = status;
      void _exhaustive;
      return null;
    }
  }
}
